// =====================================================================
// paginas/conta.js — Minha conta: foto, telefone e 2FA.
//
// O 2FA vive AQUI, e não na tela de Usuários, porque só a própria pessoa
// pode ativá-lo: o segredo é gerado contra o token dela e o código sai do
// celular dela. Um master "ativando o 2FA de outro" seria teatro — ele não
// tem o aparelho.
//
// O que o master faz na tela de Usuários é ver quem ainda não ativou e,
// se alguém perder o celular E os códigos, RESETAR (mfa-reset) — o que não
// libera ninguém sem 2FA: obriga a cadastrar de novo.
//
// A tabela usuario_mfa NÃO é lida daqui. Ela tem RLS sem policies: o
// navegador não a enxerga, e é assim que tem que ser — o segredo não tem
// o que fazer no browser. O que a tela precisa saber (ativo? quantos
// códigos restam?) vem da função mfa_ativos(), que devolve booleano e
// contagem, e nada mais.
// =====================================================================

import { sb, api } from '../../../../packages/ui/cliente.js';
import { icon, escapeHtml, checar } from '../ui.js';

/** Estado do 2FA de quem está logado. Sem tocar no segredo. */
export async function meuMfa(userId) {
  const { data } = await sb.rpc('mfa_ativos');
  const meu = (data || []).find(m => m.user_id === userId);
  return {
    ativo: !!(meu && meu.ativo),
    codigos_restantes: meu ? meu.codigos_restantes : 0
  };
}

/** Modal "Minha conta". */
export async function minhaConta(perfil, aoSalvar) {
  const mfa = await meuMfa(perfil.id);

  const fundo = document.createElement('div');
  fundo.className = 'modal-fundo';
  fundo.innerHTML = `
    <div class="modal">
      <header>
        <h3>Minha conta</h3>
        <button class="icone" data-fechar>${icon('erro')}</button>
      </header>
      <div class="corpo">
        <div class="conta-topo">
          <div class="foto-perfil" id="previa">${perfil.foto_url ? '' : icon('pessoa')}</div>
          <div>
            <b>${escapeHtml(perfil.nome)}</b>
            <div class="dica">${escapeHtml(perfil.email)} · ${escapeHtml(perfil.papel)}</div>
            <label class="botao pequeno" style="margin-top:8px">
              ${icon('upload')} Trocar foto
              <input type="file" id="foto" accept="image/*" hidden>
            </label>
          </div>
        </div>

        <label>Telefone
          <input id="telefone" value="${escapeHtml(perfil.telefone || '')}" placeholder="(31) 99999-9999">
        </label>

        <div class="bloco-2fa">
          <div class="linha-2fa">
            <div>
              <b>${icon('seguranca')} Verificação em duas etapas</b>
              <div class="dica">${mfa.ativo
                ? `Ativa. ${mfa.codigos_restantes} código(s) de recuperação disponível(is).`
                : 'Uma senha vazada não basta para entrar se o 2FA estiver ativo.'}</div>
            </div>
            ${mfa.ativo
              ? `<button id="b-codigos">Novos códigos</button>`
              : `<button class="primaria" id="b-ativar">Ativar 2FA</button>`}
          </div>
          ${mfa.ativo && mfa.codigos_restantes <= 2 ? `
            <div class="aviso atencao" style="margin:12px 0 0">${icon('alerta')}
              Restam ${mfa.codigos_restantes} código(s). Se o celular sumir e eles acabarem,
              só um outro master consegue te devolver o acesso.</div>` : ''}
          <div id="area-2fa"></div>
        </div>

        <div id="msg"></div>
      </div>
      <footer>
        <button data-fechar>Fechar</button>
        <button class="primaria" id="salvar">Salvar</button>
      </footer>
    </div>`;

  document.body.appendChild(fundo);
  const fechar = () => fundo.remove();
  fundo.querySelectorAll('[data-fechar]').forEach(b => (b.onclick = fechar));
  fundo.onclick = (e) => { if (e.target === fundo) fechar(); };

  const msg = (txt, cls = 'erro') => {
    fundo.querySelector('#msg').innerHTML = `<div class="aviso ${cls}">${txt}</div>`;
  };

  // ── Foto ──────────────────────────────────────────────────────────
  const previa = fundo.querySelector('#previa');
  if (perfil.foto_url) mostrarFoto(previa, perfil.foto_url);

  let arquivoFoto = null;
  fundo.querySelector('#foto').onchange = (e) => {
    arquivoFoto = e.target.files[0];
    if (!arquivoFoto) return;
    if (arquivoFoto.size > 5 * 1024 * 1024) {
      arquivoFoto = null;
      return msg('A foto passa de 5 MB. Use uma menor.');
    }
    previa.innerHTML = `<img src="${URL.createObjectURL(arquivoFoto)}" alt="">`;
  };

  // ── Ativar o 2FA ──────────────────────────────────────────────────
  const bAtivar = fundo.querySelector('#b-ativar');
  if (bAtivar) bAtivar.onclick = async () => {
    const area = fundo.querySelector('#area-2fa');
    area.innerHTML = '<div class="carregando">Gerando o QR Code…</div>';

    try {
      const r = await api('mfa-setup', { acao: 'iniciar' });

      area.innerHTML = `
        <div class="qr-2fa">
          <img src="${r.qr}" alt="QR Code do 2FA" width="190" height="190">
          <div>
            <p><b>1.</b> Abra o Google Authenticator e leia o código.</p>
            <p><b>2.</b> Não consegue ler? Digite: <code>${escapeHtml(r.segredo)}</code></p>
            <p><b>3.</b> Confirme o número de 6 dígitos:</p>
            <div class="conf-2fa">
              <input id="cod" inputmode="numeric" maxlength="6" placeholder="000000">
              <button class="primaria" id="b-conf">Confirmar</button>
            </div>
            <p class="dica">O 2FA só liga depois que você acertar o código —
               ninguém fica trancado para fora por ter lido o QR errado.</p>
          </div>
        </div>`;

      area.querySelector('#b-conf').onclick = async () => {
        const codigo = area.querySelector('#cod').value.trim();
        try {
          const c = await api('mfa-setup', { acao: 'confirmar', codigo });
          area.innerHTML = '';
          // Os 8 códigos aparecem uma vez. Só uma.
          mostrarCodigos(fundo, c.backup_codes, perfil.email, () => {
            fechar();
            if (aoSalvar) aoSalvar();
          });
        } catch (e) {
          msg(escapeHtml(e.message));
        }
      };
    } catch (e) {
      area.innerHTML = '';
      msg(escapeHtml(e.message));
    }
  };

  // ── Novos códigos de backup ───────────────────────────────────────
  const bCodigos = fundo.querySelector('#b-codigos');
  if (bCodigos) bCodigos.onclick = () => {
    const area = fundo.querySelector('#area-2fa');
    area.innerHTML = `
      <div class="aviso info" style="margin-top:12px">${icon('info')}
        Gerar novos códigos <b>invalida os antigos</b>. O papel que você imprimiu
        deixa de valer.</div>
      <div class="conf-2fa">
        <input id="cod2" inputmode="numeric" maxlength="6" placeholder="000000">
        <button class="primaria" id="b-gerar">Gerar novos</button>
      </div>
      <p class="dica">Digite o código do app: quem não tem o celular na mão não gera
         códigos de recuperação. Sem isso, uma sessão roubada viraria acesso permanente.</p>`;

    area.querySelector('#b-gerar').onclick = async () => {
      const codigo = area.querySelector('#cod2').value.trim();
      try {
        const r = await api('mfa-setup', { acao: 'novos-codigos', codigo });
        area.innerHTML = '';
        mostrarCodigos(fundo, r.backup_codes, perfil.email, () => {
          fechar();
          if (aoSalvar) aoSalvar();
        });
      } catch (e) {
        msg(escapeHtml(e.message));
      }
    };
  };

  // ── Salvar ────────────────────────────────────────────────────────
  fundo.querySelector('#salvar').onclick = async () => {
    const btn = fundo.querySelector('#salvar');
    btn.disabled = true;
    btn.textContent = 'Salvando…';
    try {
      const patch = { telefone: fundo.querySelector('#telefone').value.trim() || null };

      if (arquivoFoto) {
        const ext = (arquivoFoto.name.split('.').pop() || 'jpg').toLowerCase();
        const caminho = `${perfil.tenant_id}/${perfil.id}.${ext}`;
        const { error } = await sb.storage.from('perfis')
          .upload(caminho, arquivoFoto, { upsert: true, contentType: arquivoFoto.type });
        if (error) throw new Error(error.message);
        patch.foto_url = caminho;
      }

      checar(await sb.from('perfis').update(patch).eq('id', perfil.id));
      fechar();
      if (aoSalvar) aoSalvar();
    } catch (e) {
      btn.disabled = false;
      btn.textContent = 'Salvar';
      msg(escapeHtml(e.message));
    }
  };
}

// ─── Os códigos de recuperação: existem uma vez ───────────────────────
function mostrarCodigos(fundo, codigos, email, aoFechar) {
  const corpo = fundo.querySelector('.corpo');
  corpo.innerHTML = `
    <div class="aviso atencao">${icon('alerta')}
      <div><b>Guarde agora — eles não aparecem de novo.</b>
      Se o celular sumir, é com eles que você entra. Cada um serve uma vez só.</div>
    </div>
    <div class="codigos-backup">${codigos.map(c => `<code>${escapeHtml(c)}</code>`).join('')}</div>
    <div style="display:flex;gap:8px;margin-top:12px">
      <button id="p-imprimir" style="flex:1">${icon('pdf')} Imprimir</button>
      <button id="p-copiar" style="flex:1">${icon('documentos')} Copiar</button>
    </div>
    <label style="margin-top:14px;display:flex;gap:9px;align-items:flex-start">
      <input type="checkbox" id="p-guardei" style="width:auto;margin-top:3px">
      <span>Guardei os códigos num lugar seguro.</span>
    </label>`;

  const rodape = fundo.querySelector('footer');
  rodape.innerHTML = `<button class="primaria" id="p-ok" disabled>Concluir</button>`;

  corpo.querySelector('#p-imprimir').onclick = () => imprimirCodigos(codigos, email);
  corpo.querySelector('#p-copiar').onclick = async () => {
    await navigator.clipboard.writeText(codigos.join('\n'));
    corpo.querySelector('#p-copiar').textContent = 'Copiado';
  };

  const chk = corpo.querySelector('#p-guardei');
  const ok = rodape.querySelector('#p-ok');
  chk.onchange = () => { ok.disabled = !chk.checked; };
  ok.onclick = aoFechar;
}

export function imprimirCodigos(codigos, email) {
  const j = window.open('', '_blank');
  if (!j) return alert('O navegador bloqueou a janela de impressão.');
  j.document.write(`
    <html><head><title>Códigos de recuperação — Smart Gestão Imobiliária</title>
    <style>
      body{font:15px/1.7 -apple-system,sans-serif;padding:40px;max-width:640px}
      h1{font-size:19px;margin:0 0 6px}
      code{display:inline-block;font-size:19px;letter-spacing:2px;border:1px solid #ccc;
           border-radius:6px;padding:9px 14px;margin:5px}
      .aviso{color:#92400E;background:#FEF3C7;padding:12px;border-radius:8px}
    </style></head><body>
    <h1>Códigos de recuperação — Smart Gestão Imobiliária</h1>
    <p>Conta: <b>${escapeHtml(email || '')}</b> · Gerados em ${new Date().toLocaleDateString('pt-BR')}</p>
    <p class="aviso">Cada código serve <b>uma única vez</b>. Guarde este papel em local seguro:
       quem tiver estes códigos e a sua senha entra na sua conta.</p>
    <p>${codigos.map(c => `<code>${escapeHtml(c)}</code>`).join(' ')}</p>
    </body></html>`);
  j.document.close();
  j.print();
}

/** Foto do bucket privado: URL assinada, curta. */
export async function mostrarFoto(el, caminho) {
  const { data } = await sb.storage.from('perfis').createSignedUrl(caminho, 3600);
  if (data && data.signedUrl) el.innerHTML = `<img src="${data.signedUrl}" alt="">`;
}
